import React from 'react';
import { graphql, useFragment, useMutation } from 'react-relay';
import Toggle from '@atlaskit/toggle';
import { Label } from '@atlaskit/form';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { type UIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';
import useDebouncedCallback from '@atlassian/jira-platform-use-debounce/src/utils/use-debounce-callback/index.tsx';

import { useIntl } from '@atlassian/jira-intl';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { useShowErrorFlag } from '@atlassian/jira-personal-settings-error-flag/src/ui/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';

import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment.graphql';
import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment.graphql';
import type { combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment$key } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment.graphql';

import type { combinedInviterNotifEmailPref_personalSettingsDigestNotificationsMutation } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotifEmailPref_personalSettingsDigestNotificationsMutation.graphql';
import type { combinedInviterNotifInProductPref_personalSettingsDigestNotificationsMutation } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotifInProductPref_personalSettingsDigestNotificationsMutation.graphql';
import type { combinedInviterNotifSlackPref_personalSettingsDigestNotificationsMutation } from '@atlassian/jira-relay/src/__generated__/combinedInviterNotifSlackPref_personalSettingsDigestNotificationsMutation.graphql';

import messages from '../../../ui/messages.tsx';

export const CombinedInviterNotification = ({
	onToggleChange,
	emailFragment,
	inProductFragment,
	slackFragment,
}: {
	onToggleChange: (isEnabled: boolean) => void;
	emailFragment: combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment$key;
	inProductFragment: combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment$key;
	slackFragment: combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment$key;
}) => {
	const { formatMessage } = useIntl();
	const cloudId = useCloudId();
	const { showErrorFlag } = useShowErrorFlag();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const emailData = useFragment(
		graphql`
			fragment combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment on JiraNotificationPreferences {
				projectInviterNotification {
					id @required(action: THROW)
					emailChannel {
						isEnabled
					}
				}
			}
		`,
		emailFragment,
	);

	const inProductData = useFragment(
		graphql`
			fragment combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment on JiraNotificationPreferences {
				projectInviterNotification {
					id @required(action: THROW)
					inProductChannel {
						isEnabled
					}
				}
			}
		`,
		inProductFragment,
	);

	const slackData = useFragment(
		graphql`
			fragment combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment on JiraNotificationPreferences {
				projectInviterNotification {
					id @required(action: THROW)
					slackChannel {
						isEnabled
					}
				}
			}
		`,
		slackFragment,
	);

	const [commitEmail] =
		useMutation<combinedInviterNotifEmailPref_personalSettingsDigestNotificationsMutation>(graphql`
			mutation combinedInviterNotifEmailPref_personalSettingsDigestNotificationsMutation(
				$cloudId: ID!
				$isEnabled: Boolean!
			) {
				jira {
					updateGlobalNotificationPreferences(
						cloudId: $cloudId
						input: {
							preferences: [
								{ channel: EMAIL, isEnabled: $isEnabled, type: PROJECT_INVITER_NOTIFICATION }
							]
						}
					) {
						success
						errors {
							message
						}
						preferences {
							...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_emailFragment
						}
					}
				}
			}
		`);

	const [commitInProduct] =
		useMutation<combinedInviterNotifInProductPref_personalSettingsDigestNotificationsMutation>(
			graphql`
				mutation combinedInviterNotifInProductPref_personalSettingsDigestNotificationsMutation(
					$cloudId: ID!
					$isEnabled: Boolean!
				) {
					jira {
						updateGlobalNotificationPreferences(
							cloudId: $cloudId
							input: {
								preferences: [
									{ channel: IN_PRODUCT, isEnabled: $isEnabled, type: PROJECT_INVITER_NOTIFICATION }
								]
							}
						) {
							success
							errors {
								message
							}
							preferences {
								...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_inProductFragment
							}
						}
					}
				}
			`,
		);

	const [commitSlack] =
		useMutation<combinedInviterNotifSlackPref_personalSettingsDigestNotificationsMutation>(graphql`
			mutation combinedInviterNotifSlackPref_personalSettingsDigestNotificationsMutation(
				$cloudId: ID!
				$isEnabled: Boolean!
			) {
				jira {
					updateGlobalNotificationPreferences(
						cloudId: $cloudId
						input: {
							preferences: [
								{ channel: SLACK, isEnabled: $isEnabled, type: PROJECT_INVITER_NOTIFICATION }
							]
						}
					) {
						success
						errors {
							message
						}
						preferences {
							...combinedInviterNotif_personalSettingsDigestNotifications_CombinedInviterNotification_slackFragment
						}
					}
				}
			}
		`);

	const [debouncedHandleToggleChange] = useDebouncedCallback(
		(e: React.ChangeEvent<HTMLInputElement>, analyticsEventUI: UIAnalyticsEvent) => {
			const newValue = e.target.checked;
			onToggleChange(newValue);
			fireUIAnalytics(analyticsEventUI, 'toggleInviterNotificationSwitch', {
				isToggleOn: newValue,
				channel: 'combined',
			});

			// Commit all mutations in parallel as we can't make a single mutation for three notification channels
			Promise.all([
				new Promise((resolve, reject) => {
					commitEmail({
						variables: {
							cloudId,
							isEnabled: newValue,
						},
						optimisticResponse: {
							jira: {
								updateGlobalNotificationPreferences: {
									success: true,
									errors: [],
									preferences: {
										projectInviterNotification: {
											id: emailData.projectInviterNotification?.id ?? '',
											emailChannel: {
												isEnabled: newValue,
											},
										},
									},
								},
							},
						},
						onError: (error: Error) => {
							showErrorFlag();
							fireErrorAnalytics({
								meta: {
									id: 'mutateInviterNotifEmailPreference',
									packageName: 'jiraPersonalSettingsNotificationsDigests',
									teamName: 'jira-warepil',
								},
								error,
								sendToPrivacyUnsafeSplunk: true,
							});
							reject(error);
						},
						onCompleted: (response) => {
							const errors = response?.jira?.updateGlobalNotificationPreferences?.errors;
							if (errors && errors.length > 0) {
								showErrorFlag();
								errors.forEach((error) => {
									fireErrorAnalytics({
										meta: {
											id: 'mutateInviterNotifEmailPreferenceCompletedError',
											packageName: 'jiraPersonalSettingsNotificationsDigests',
											teamName: 'jira-warepil',
										},
										error: new Error(error?.message || 'Unknown Error'),
										sendToPrivacyUnsafeSplunk: true,
									});
								});
							} else {
								fireOperationalAnalytics(
									createAnalyticsEvent({}),
									'mutateInviterNotifEmailPreference success',
								);
							}
							resolve(response);
						},
					});
				}),
				new Promise((resolve, reject) => {
					commitInProduct({
						variables: {
							cloudId,
							isEnabled: newValue,
						},
						optimisticResponse: {
							jira: {
								updateGlobalNotificationPreferences: {
									success: true,
									errors: [],
									preferences: {
										projectInviterNotification: {
											id: inProductData.projectInviterNotification?.id ?? '',
											inProductChannel: {
												isEnabled: newValue,
											},
										},
									},
								},
							},
						},
						onError: (error: Error) => {
							showErrorFlag();
							fireErrorAnalytics({
								meta: {
									id: 'mutateInviterNotifInProductPreference',
									packageName: 'jiraPersonalSettingsNotificationsDigests',
									teamName: 'jira-warepil',
								},
								error,
								sendToPrivacyUnsafeSplunk: true,
							});
							reject(error);
						},
						onCompleted: (response) => {
							const errors = response?.jira?.updateGlobalNotificationPreferences?.errors;
							if (errors && errors.length > 0) {
								showErrorFlag();
								errors.forEach((error) => {
									fireErrorAnalytics({
										meta: {
											id: 'mutateInviterNotifInProductPreferenceCompletedError',
											packageName: 'jiraPersonalSettingsNotificationsDigests',
											teamName: 'jira-warepil',
										},
										error: new Error(error?.message || 'Unknown Error'),
										sendToPrivacyUnsafeSplunk: true,
									});
								});
							} else {
								fireOperationalAnalytics(
									createAnalyticsEvent({}),
									'mutateInviterNotifInProductPreference success',
								);
							}
							resolve(response);
						},
					});
				}),
				new Promise((resolve, reject) => {
					commitSlack({
						variables: {
							cloudId,
							isEnabled: newValue,
						},
						optimisticResponse: {
							jira: {
								updateGlobalNotificationPreferences: {
									success: true,
									errors: [],
									preferences: {
										projectInviterNotification: {
											id: slackData.projectInviterNotification?.id ?? '',
											slackChannel: {
												isEnabled: newValue,
											},
										},
									},
								},
							},
						},
						onError: (error: Error) => {
							showErrorFlag();
							fireErrorAnalytics({
								meta: {
									id: 'mutateinviterNotifInProductPreference',
									packageName: 'jiraPersonalSettingsNotificationsDigests',
									teamName: 'jira-warepil',
								},
								error,
								sendToPrivacyUnsafeSplunk: true,
							});
							reject(error);
						},
						onCompleted: (response) => {
							const errors = response?.jira?.updateGlobalNotificationPreferences?.errors;
							if (errors && errors.length > 0) {
								showErrorFlag();
								errors.forEach((error) => {
									fireErrorAnalytics({
										meta: {
											id: 'mutateinviterNotifInProductPreferenceCompletedError',
											packageName: 'jiraPersonalSettingsNotificationsDigests',
											teamName: 'jira-warepil',
										},
										error: new Error(error?.message || 'Unknown Error'),
										sendToPrivacyUnsafeSplunk: true,
									});
								});
							} else {
								fireOperationalAnalytics(
									createAnalyticsEvent({}),
									'mutateinviterNotifInProductPreference success',
								);
							}
							resolve(response);
						},
					});
				}),
			]).catch((error: Error) => {
				fireErrorAnalytics({
					meta: {
						id: 'mutateInviterNotifInProductPreferenceCompletedError',
						packageName: 'jiraPersonalSettingsNotificationsDigests',
						teamName: 'jira-warepil',
					},
					error: new Error(error?.message || 'Unknown Error'),
					sendToPrivacyUnsafeSplunk: true,
				});
			});
		},
		300,
		{ leading: true, trailing: true },
	);

	return (
		<Box xcss={[headerWrapperStyles, enabledHeaderWrapperStyles]}>
			<Inline spread="space-between" alignBlock="center">
				<Label htmlFor="due-date-notifs-enabled">
					<Inline alignBlock="center" xcss={labelHolderStyles}>
						<Inline
							testId="personal-settings-digest-notifications.common.ui.combined-inviter-notif.toggle"
							xcss={headerLabelStyles}
							alignBlock="center"
						>
							{formatMessage(messages.digestsNotificationsProjectInviter)}
						</Inline>
					</Inline>
				</Label>
				<Toggle
					id="inviter-notifs-enabled"
					isChecked={
						!!inProductData.projectInviterNotification?.inProductChannel?.isEnabled ||
						!!emailData.projectInviterNotification?.emailChannel?.isEnabled ||
						!!slackData.projectInviterNotification?.slackChannel?.isEnabled ||
						false
					}
					onChange={(e, analyticsEventUI) => debouncedHandleToggleChange(e, analyticsEventUI)}
				/>
			</Inline>
		</Box>
	);
};

const headerLabelStyles = xcss({
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	color: 'color.text',
});

const headerWrapperStyles = xcss({
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius.100',
	paddingRight: 'space.150',
	paddingLeft: 'space.200',
	paddingTop: 'space.100',
	paddingBottom: 'space.100',
});

const enabledHeaderWrapperStyles = xcss({
	borderBottomLeftRadius: '0px',
	borderBottomRightRadius: '0px',
	backgroundColor: 'elevation.surface.sunken',
});

const labelHolderStyles = xcss({
	marginTop: 'space.050',
	gap: 'space.100',
});
